import defaults from './defaults';
import { modulo } from './utils';

export default function Tabs(element, options) {
	const config = { ...defaults, ...options };
	const tablist = document.getElementById(element);

	if (!tablist) {
		return;
	}

	let orientation;
	let currentIndex;
	let tabsArray = [];
	let initiated = false;

	function create() {
		if (initiated) {
			return;
		}

		let tabElements = [];

		tablist.setAttribute('role', 'tablist');
		tablist.classList.add(config.initialisedClass);
		setOrientation(config.orientation);

		Array.from(tablist.getElementsByTagName('li')).forEach(listItem => {
			tabElements.push(listItem.firstElementChild);
			listItem.setAttribute('role', 'presentation');
		});

		tabElements.forEach(tab => createTab(tab));

		function createTab(tab) {
			let id = tab.hash.substr(1);
			let panel = document.getElementById(id);

			const tabId = tab.id ? tab.id : `tab-${id}`;

			if (!tab.id) {
				tab.id = tabId;
			}

			tab.setAttribute('role', 'tab');
			tab.setAttribute('tabindex', -1);
			tab.setAttribute('aria-selected', false);
			tab.setAttribute('aria-controls', panel.id);
			tab.addEventListener('click', onClick);
			tab.addEventListener('keydown', onKeydown);

			panel.setAttribute('role', 'tabpanel');
			panel.setAttribute('aria-hidden', true);
			panel.setAttribute('aria-labelledby', tabId);

			tabsArray.push({ id, tab, panel, selected: false });
		}

		initiated = true;

		select(config.selected, false);

		dispatchEvent('create');
	}

	function destroy() {
		if (!initiated) {
			return;
		}

		tablist.removeAttribute('role');
		tablist.classList.remove(config.initialisedClass);

		Array.from(tablist.getElementsByTagName('li')).forEach(function(element) {
			element.removeAttribute('role');
		});

		tabsArray.forEach(function(item) {
			const { tab, panel } = item;

			tab.removeAttribute('role');
			tab.removeAttribute('tabindex');
			tab.removeAttribute('aria-selected');
			tab.removeAttribute('aria-controls');
			tab.removeEventListener('click', onClick);
			tab.removeEventListener('keydown', onKeydown);

			panel.removeAttribute('role');
			panel.removeAttribute('aria-hidden');
			panel.removeAttribute('aria-labelledby');
		});

		tabsArray = [];
		initiated = false;

		dispatchEvent('destroy');
	}

	/**
	 * Select a given tab.
	 *
	 * @param {number|index} target The tab id or index
	 * @param {boolean} focus
	 */
	function select(target, focus = true) {
		const index = getIndex(target);
		const { tab, panel, selected } = tabsArray[index];

		if (selected) {
			return;
		}

		if (currentIndex !== undefined) {
			deselect(currentIndex);
		}

		tab.setAttribute('tabindex', 0);
		tab.setAttribute('aria-selected', true);
		tab.classList.add(config.selectedTabClass);

		panel.setAttribute('aria-hidden', false);
		panel.classList.add(config.selectedPanelClass);

		tabsArray[index].selected = true;

		if (focus) {
			tab.focus();
		}

		currentIndex = index;

		dispatchEvent('change', { ...tabsArray[index], index });
	}

	/**
	 * Deselect a given tab.
	 *
	 * @param {number|string} index The tab id or index
	 */
	function deselect(index) {
		const i = getIndex(index);
		const { tab, panel } = tabsArray[i];

		tab.setAttribute('tabindex', -1);
		tab.setAttribute('aria-selected', false);
		tab.classList.remove(config.selectedTabClass);

		panel.setAttribute('aria-hidden', true);
		panel.classList.remove(config.selectedPanelClass);

		tabsArray[index].selected = false;
	}

	/**
	 * Get the selected tab.
	 *
	 * @returns {object}
	 */
	function getSelected() {
		return tabsArray[currentIndex];
	}

	/**
	 * Select previous tab.
	 */
	function previous() {
		select(modulo(currentIndex - 1, tabsArray.length));
	}

	/**
	 * Select next tab.
	 */
	function next() {
		select(modulo(currentIndex + 1, tabsArray.length));
	}

	/**
	 * Gets the tab index from either a number or the tab id.
	 *
	 * @param {number|string} index The tab id or index
	 */
	function getIndex(index) {
		let i = undefined;

		if (typeof index == 'number') {
			i = index;
		} else if (typeof index == 'string') {
			i = tabsArray.findIndex(x => x.id == index);
		} else {
			new TypeError(`Expected Number or String, was ${typeof index}`);
		}

		if (!tabsArray[i]) {
			throw new Error(`No Tab with the index of ${index}`);
		} else {
			return i;
		}
	}

	/**
	 * Set tablist orientation.
	 *
	 * @param {string} value
	 */
	function setOrientation(value) {
		if (value != 'horizontal' && value != 'vertical') {
			return;
		}

		orientation = value;
		tablist.setAttribute('aria-orientation', orientation);

		return orientation;
	}

	/**
	 * Handle tab click event.
	 *
	 * @param {Event} event
	 */
	function onClick(event) {
		const tab = event.currentTarget;
		const panel = tab.getAttribute('aria-controls');

		select(panel);
		event.preventDefault();
	}

	/**
	 * Handle tab key event.
	 *
	 * @param {Event} event
	 */
	function onKeydown(event) {
		const isVertical = orientation == 'vertical';
		const isModifierKey = event.metaKey || event.altKey;

		if (event.key === 'ArrowLeft' && !isVertical && !isModifierKey) {
			previous();
		}

		if (event.key === 'ArrowRight' && !isVertical && !isModifierKey) {
			next();
		}

		if (event.key == 'ArrowUp' && isVertical) {
			previous();
			event.preventDefault();
		}

		if (event.key == 'ArrowDown' && isVertical) {
			next();
			event.preventDefault();
		}
	}

	/**
	 * Wrapper method to add an event listener
	 *
	 * @param {string} type
	 * @param {function} handler
	 */
	function on(type, handler) {
		tablist.addEventListener(type, handler);
	}

	/**
	 * Wrapper method to remove an event listener
	 *
	 * @param {string} type
	 * @param {function} handler
	 */
	function off(type, handler) {
		tablist.removeEventListener(type, handler);
	}

	/**
	 * Dispatches a custom event
	 *
	 * @param {string} type
	 * @return {boolean}
	 */
	function dispatchEvent(type, detail = undefined) {
		let event = new CustomEvent(`${config.eventTypePrefix}${type}`, {
			bubbles: true,
			cancelable: true,
			detail: detail,
		});

		return tablist.dispatchEvent(event);
	}

	return { create, destroy, getSelected, next, off, on, orientation, previous, select, setOrientation };
}
